import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ModulesManager } from "@/components/ModulesManager";
import { Building2, Boxes, ClipboardCheck, Search, Plus, LogOut, ChevronRight, ArrowUpDown, Store, Package, Users, KeyRound } from "lucide-react";

const NAV = [
  { id: "organisations", label: "Organisations", icon: Building2 },
  { id: "modules", label: "Modules", icon: Building2 },
  { id: "catalogue", label: "Catalogue", icon: Boxes },
  { id: "onboarding", label: "Onboarding", icon: ClipboardCheck },
];

export default function SuperAdminPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("organisations");
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null); // full org object
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setOrgs((await api.get("/admin/organisations")).data); }
    catch { toast.error("Failed to load organisations"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="superadmin-portal">
      {/* Persistent left nav */}
      <aside className="w-60 bg-slate-900 text-white flex flex-col fixed inset-y-0 left-0">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="font-display font-extrabold text-lg">Cater<span className="text-brand">CTRL</span></div>
          <div className="text-[11px] uppercase tracking-wide text-white/50">Superadmin Portal</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => { setView(n.id); setSelected(null); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${view === n.id ? "bg-brand text-white" : "text-white/70 hover:bg-white/10"}`}>
              <n.icon size={18} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="text-xs text-white/60 mb-2 px-1">{user?.name} · superadmin</div>
          <button data-testid="portal-change-password" onClick={() => navigate("/change-password")} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/10"><KeyRound size={16} /> Change password</button>
          <button data-testid="portal-logout" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/10"><LogOut size={16} /> Log out</button>
        </div>
      </aside>

      <main className="flex-1 ml-60 flex flex-col">
        {/* Context bar — always shows current customer/site */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-8 py-3 flex items-center gap-3" data-testid="context-bar">
          <span className="text-xs uppercase tracking-wide text-slate-400">Context</span>
          {selected ? (
            <>
              <span className="font-semibold text-slate-800" data-testid="ctx-org">{selected.name}</span>
              <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-500 capitalize">{selected.onboarding_status}</span>
              <button className="text-xs text-brand-dark ml-auto" data-testid="ctx-clear" onClick={() => setSelected(null)}>← All organisations</button>
            </>
          ) : <span className="text-slate-400 text-sm" data-testid="ctx-none">No organisation selected — pick one to manage its sites, users and catalogue.</span>}
        </div>

        <div className="p-8 flex-1">
          {selected ? <OrgWorkspace org={selected} onChanged={load} /> :
            view === "organisations" ? <OrgTable orgs={orgs} loading={loading} onOpen={setSelected} onCreated={load} /> :
            view === "catalogue" ? <CataloguePicker orgs={orgs} onOpen={setSelected} /> :
            view === "modules" ? <ModulesManager orgs={orgs} /> :
            <OnboardingBoard orgs={orgs} onOpen={setSelected} />}
        </div>
      </main>
    </div>
  );
}

function OrgTable({ orgs, loading, onOpen, onCreated }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ k: "created_at", dir: -1 });
  const [creating, setCreating] = useState(false);
  const rows = useMemo(() => {
    let r = orgs.filter((o) => o.name?.toLowerCase().includes(q.toLowerCase()));
    r = [...r].sort((a, b) => { const av = a[sort.k] ?? "", bv = b[sort.k] ?? ""; return (av > bv ? 1 : av < bv ? -1 : 0) * sort.dir; });
    return r;
  }, [orgs, q, sort]);
  const th = (k, label) => <th className="text-left px-3 py-2 font-semibold text-slate-500 cursor-pointer select-none" onClick={() => setSort((s) => ({ k, dir: s.k === k ? -s.dir : 1 }))} data-testid={`sort-${k}`}>{label} <ArrowUpDown size={12} className="inline text-slate-300" /></th>;
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <h1 className="font-display text-2xl font-bold text-slate-800">Organisations</h1>
        <div className="relative ml-auto w-72"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search organisations…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="org-search" className="pl-9 bg-white" /></div>
        <Button className="bg-brand hover:bg-brand-hover" data-testid="new-org-btn" onClick={() => setCreating(true)}><Plus size={16} className="mr-1" /> New</Button>
      </div>
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm" data-testid="org-table">
          <thead className="bg-slate-50 border-b"><tr>{th("name", "Organisation")}{th("onboarding_status", "Status")}{th("venue_count", "Sites")}{th("user_count", "Users")}{th("onboarding_progress", "Onboarding")}{th("created_at", "Created")}<th></th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No organisations.</td></tr>}
            {rows.map((o) => (
              <tr key={o.org_id} className="border-b hover:bg-slate-50 cursor-pointer" data-testid={`org-row-${o.org_id}`} onClick={() => onOpen(o)}>
                <td className="px-3 py-2.5 font-semibold text-slate-800">{o.name}</td>
                <td className="px-3 py-2.5"><span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">{o.onboarding_status}</span></td>
                <td className="px-3 py-2.5">{o.venue_count}</td>
                <td className="px-3 py-2.5">{o.user_count}</td>
                <td className="px-3 py-2.5">{o.onboarding_progress != null ? `${o.onboarding_progress}%` : "—"}</td>
                <td className="px-3 py-2.5 text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2.5 text-right"><ChevronRight size={16} className="text-slate-300" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && <NewOrg onClose={() => setCreating(false)} onDone={() => { setCreating(false); onCreated(); }} />}
    </div>
  );
}

function NewOrg({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", admin_name: "", admin_email: "" });
  const save = async () => {
    if (!f.name || !f.admin_email) return toast.error("Business name and admin email required");
    try { await api.post("/admin/organisations", f); toast.success("Organisation created & admin invited"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-96 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="new-org-modal">
        <h3 className="font-display font-bold text-slate-800">New organisation</h3>
        <div><Label className="text-xs">Business name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="org-name" className="mt-1" /></div>
        <div><Label className="text-xs">Primary admin name</Label><Input value={f.admin_name} onChange={(e) => setF({ ...f, admin_name: e.target.value })} data-testid="org-admin-name" className="mt-1" /></div>
        <div><Label className="text-xs">Primary admin email</Label><Input value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} data-testid="org-admin-email" className="mt-1" /></div>
        <div className="flex justify-end gap-2 pt-1"><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-brand hover:bg-brand-hover" onClick={save} data-testid="create-org">Create & invite</Button></div>
      </div>
    </div>
  );
}

function OrgWorkspace({ org, onChanged }) {
  const [tab, setTab] = useState("sites");
  const [data, setData] = useState({ venues: [], users: [], suppliers: [], products: [], onboarding: null });
  const [siteModal, setSiteModal] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const loadedRef = useRef(null);
  const load = async () => {
    try {
      const [v, u, s, p, ob] = await Promise.all([
        api.get(`/admin/organisations/${org.org_id}/venues`),
        api.get(`/admin/organisations/${org.org_id}/users`),
        api.get(`/admin/organisations/${org.org_id}/suppliers`),
        api.get(`/admin/organisations/${org.org_id}/products`),
        api.get(`/admin/organisations/${org.org_id}/onboarding`).catch(() => ({ data: null })),
      ]);
      setData({ venues: v.data, users: u.data, suppliers: s.data, products: p.data, onboarding: ob.data });
    } catch { toast.error("Failed to load workspace data"); }
  };
  // Guard against React StrictMode double-invoke: fetch once per org.
  useEffect(() => {
    if (loadedRef.current === org.org_id) return;
    loadedRef.current = org.org_id;
    load();
  }, [org.org_id]);
  const TABS = [["sites", "Sites", Store], ["users", "Users & invites", Users], ["suppliers", "Suppliers", Store], ["products", "Products", Package], ["onboarding", "Onboarding", ClipboardCheck]];

  const afterWrite = () => { load(); onChanged(); };
  const progress = data.onboarding?.progress;

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-slate-800 mb-1">{org.name}</h1>
      <div className="flex gap-2 border-b mb-4">
        {TABS.map(([id, label]) => <button key={id} data-testid={`ws-tab-${id}`} onClick={() => setTab(id)} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === id ? "border-brand text-brand-dark" : "border-transparent text-slate-500"}`}>{label}</button>)}
      </div>

      {tab === "sites" && (
        <div className="bg-white rounded-xl border p-4">
          <div className="flex justify-between mb-2"><h3 className="font-semibold text-slate-700">Sites / kitchens</h3><Button size="sm" className="bg-brand hover:bg-brand-hover" data-testid="ws-add-site" onClick={() => setSiteModal(true)}><Plus size={14} className="mr-1" />Add site</Button></div>
          {data.venues.length === 0 ? <p className="text-sm text-slate-400">No sites yet.</p> :
            <ul className="divide-y">{data.venues.map((v) => <li key={v.venue_id} className="py-2 text-sm text-slate-700" data-testid={`ws-site-${v.venue_id}`}>{v.name}</li>)}</ul>}
        </div>
      )}
      {tab === "users" && (
        <div className="bg-white rounded-xl border p-4">
          <div className="flex justify-between mb-2"><h3 className="font-semibold text-slate-700">Users & invitations</h3><Button size="sm" className="bg-brand hover:bg-brand-hover" data-testid="ws-invite" onClick={() => setInviteModal(true)}><Plus size={14} className="mr-1" />Invite</Button></div>
          <table className="w-full text-sm"><thead className="text-slate-400 text-left"><tr><th className="py-1">Name</th><th>Email</th><th>Role</th><th>Status</th></tr></thead>
            <tbody>{data.users.map((u) => <tr key={u.user_id} className="border-t" data-testid={`ws-user-${u.user_id}`}><td className="py-1.5">{u.name}</td><td className="text-slate-500">{u.email}</td><td className="capitalize">{u.role}</td><td><span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 capitalize">{u.account_status || (u.active ? "active" : "invited")}</span></td></tr>)}</tbody></table>
        </div>
      )}
      {tab === "suppliers" && <SimpleList title="Suppliers" items={data.suppliers} keyf="supplier_id" render={(s) => `${s.name}${(s.site_ids||[]).length ? ` · ${s.site_ids.length} site(s)` : " · org-wide"}`} />}
      {tab === "products" && <SimpleList title="Products" items={data.products} keyf="product_id" render={(p) => `${p.name}${p.price!=null?` · £${Number(p.price).toFixed(2)}`:""}${(p.site_ids||[]).length ? ` · ${p.site_ids.length} site(s)` : " · org-wide"}`} />}
      {tab === "onboarding" && (
        <div className="bg-white rounded-xl border p-4" data-testid="ws-onboarding">
          <h3 className="font-semibold text-slate-700 mb-2" data-testid="ws-onboarding-progress">Onboarding progress {progress!=null?`— ${progress}%`:""}</h3>
          {data.onboarding?.steps ? <ul className="space-y-1.5">{data.onboarding.steps.map((c, i) => (
            <li key={c.key || i} className="flex items-center gap-2 text-sm" data-testid={`ob-item-${i}`}><span className={`h-4 w-4 rounded-full ${c.done ? "bg-brand" : "bg-slate-200"}`} /> <span className={c.done ? "text-slate-700" : "text-slate-400"}>{c.label}</span></li>
          ))}</ul> : <p className="text-sm text-slate-400">No onboarding data.</p>}
          <p className="text-[11px] text-slate-400 mt-3">Full catalogue import & historical data tools remain available in the classic <a href="/admin" className="text-brand-dark underline">/admin</a> console.</p>
        </div>
      )}

      {siteModal && <AddSiteModal orgId={org.org_id} onClose={() => setSiteModal(false)} onDone={() => { setSiteModal(false); afterWrite(); }} />}
      {inviteModal && <InviteUserModal orgId={org.org_id} onClose={() => setInviteModal(false)} onDone={() => { setInviteModal(false); afterWrite(); }} />}
    </div>
  );
}

function AddSiteModal({ orgId, onClose, onDone }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Site name is required");
    setSaving(true);
    try { await api.post(`/admin/organisations/${orgId}/venues`, { name: name.trim() }); toast.success("Site created"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to create site"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-96 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="add-site-modal">
        <h3 className="font-display font-bold text-slate-800">Add site / kitchen</h3>
        <div><Label className="text-xs">Site name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="site-name" className="mt-1" autoFocus /></div>
        <div className="flex justify-end gap-2 pt-1"><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-brand hover:bg-brand-hover" onClick={save} disabled={saving} data-testid="create-site">Add site</Button></div>
      </div>
    </div>
  );
}

function InviteUserModal({ orgId, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", role: "manager" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.email.trim()) return toast.error("Email is required");
    setSaving(true);
    const name = f.name.trim() || f.email.split("@")[0];
    try { await api.post(`/admin/organisations/${orgId}/users`, { name, email: f.email.trim(), role: f.role, site_access: "all", site_ids: [] }); toast.success("Invitation sent"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to send invitation"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-96 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="invite-user-modal">
        <h3 className="font-display font-bold text-slate-800">Invite user</h3>
        <div><Label className="text-xs">Name (optional)</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name" className="mt-1" /></div>
        <div><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email" className="mt-1" autoFocus /></div>
        <div><Label className="text-xs">Role</Label>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger className="mt-1" data-testid="invite-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="admin" data-testid="role-admin">Admin</SelectItem>
              <SelectItem value="manager" data-testid="role-manager">Manager</SelectItem>
              <SelectItem value="staff" data-testid="role-staff">Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2 pt-1"><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-brand hover:bg-brand-hover" onClick={save} disabled={saving} data-testid="send-invite">Send invite</Button></div>
      </div>
    </div>
  );
}

function SimpleList({ title, items, keyf, render }) {
  return (
    <div className="bg-white rounded-xl border p-4">
      <h3 className="font-semibold text-slate-700 mb-2">{title} ({items.length})</h3>
      {items.length === 0 ? <p className="text-sm text-slate-400">None assigned.</p> :
        <ul className="divide-y">{items.map((it) => <li key={it[keyf]} className="py-2 text-sm text-slate-700" data-testid={`row-${it[keyf]}`}>{render(it)}</li>)}</ul>}
    </div>
  );
}

function CataloguePicker({ orgs, onOpen }) {
  return <div><h1 className="font-display text-2xl font-bold text-slate-800 mb-2">Catalogue</h1>
    <p className="text-sm text-slate-500 mb-4">Select an organisation to assign or create suppliers & products (site-scoped or org-wide).</p>
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">{orgs.map((o) => <button key={o.org_id} data-testid={`cat-org-${o.org_id}`} onClick={() => onOpen(o)} className="bg-white border rounded-xl p-4 text-left hover:border-brand"><div className="font-semibold text-slate-800">{o.name}</div><div className="text-xs text-slate-500">{o.supplier_count ?? 0} suppliers</div></button>)}</div></div>;
}

function OnboardingBoard({ orgs, onOpen }) {
  return <div><h1 className="font-display text-2xl font-bold text-slate-800 mb-4">Onboarding</h1>
    <div className="space-y-2">{orgs.map((o) => <button key={o.org_id} data-testid={`ob-org-${o.org_id}`} onClick={() => onOpen(o)} className="w-full bg-white border rounded-xl p-3 flex items-center gap-3 hover:border-brand text-left">
      <div className="flex-1"><div className="font-semibold text-slate-800">{o.name}</div><div className="text-xs text-slate-500 capitalize">{o.onboarding_status}</div></div>
      <div className="w-40 bg-slate-100 rounded-full h-2 overflow-hidden"><div className="bg-brand h-full" style={{ width: `${o.onboarding_progress || 0}%` }} /></div>
      <span className="text-xs text-slate-500 w-10 text-right">{o.onboarding_progress || 0}%</span>
    </button>)}</div></div>;
}
